/**
 * The adaptive performance governor.
 *
 * `QUALITY` picks a tier once at boot and never looks again, which is fine
 * until round 45 at 3x with 150 bodies on screen. This closes the loop: it
 * measures every presented frame, and when the measurement says the frame is
 * costing more than the display can take it walks down a ladder of visual
 * concessions, cheapest first, one rung at a time.
 *
 * Four rules shape the whole thing.
 *
 *  1. **The tier is a ceiling, never a floor.** Every ladder rung is a *cap*
 *     over what the tier already allows. Rung 0 is exactly the tier, so a
 *     player who picks Ultra can be walked down to protect the frame rate and
 *     can never be silently walked up past their own choice.
 *  2. **Median and p95, never the mean.** A mean hides a hitch by averaging it
 *     against the twenty smooth frames either side of it. The p95 is the frame
 *     the player actually feels.
 *  3. **Asymmetric time constants.** Overrun is integrated and weighted by how
 *     badly we are over, so a 2x overrun degrades in half the time a 1x one
 *     does; recovery needs seconds of quiet plus a wide margin under budget. A
 *     governor that visibly pumps is worse than a low fixed setting, so
 *     restoring is deliberately harder than degrading.
 *  4. **Gameplay is off the table.** Nothing here touches enemy counts, the sim
 *     rate, spawn logic or hit resolution. Every rung is pixels.
 *
 * Wiring is *pull*, not push: `Renderer` feeds `sample`, and each render
 * subsystem reads `perf.caps` for itself. `core/*` is readable by everyone, so
 * no subsystem has to import another one to be governed.
 *
 * `sample` allocates nothing. Rings and the sort scratch are preallocated, the
 * ladder is module-level, and the only object churn in the file happens when a
 * rung actually changes — which is rare by construction.
 */

import { region } from './region';

/**
 * What the governor is currently willing to allow, on top of the quality tier.
 *
 * Consumers combine rather than obey: `min(tier, cap)` for the numbers,
 * `tier && cap` for the switches. That is what keeps a rung from ever raising
 * quality above the tier the player asked for.
 */
export interface PerfCaps {
  /** The generative background layer. Pure atmosphere, first thing off. */
  hydra: boolean;
  /** Multiplier on particle *spawn* counts. Pool size never changes. */
  particleScale: number;
  /** Multiplier on the swarm ribbon budget. Projectile ribbons are untouched. */
  trailScale: number;
  persistence: boolean;
  halation: boolean;
  /** Hard cap on bloom pyramid levels. */
  bloomLevelCap: number;
  /** Lattice line decimation. 2 draws every other row and column. */
  gridStride: number;
  /** Hard cap on device pixel ratio. The 640x360 buffer is never touched. */
  pixelRatioCap: number;
}

export interface PerfEvent {
  /** `performance.now` at the change. */
  t: number;
  rung: number;
  label: string;
  /** +1 degraded, -1 restored. */
  dir: 1 | -1;
}

export interface PerfSnapshot {
  enabled: boolean;
  rung: number;
  rungs: number;
  label: string;
  targetMs: number;
  budgetMs: number;
  medianFrameMs: number;
  p95FrameMs: number;
  medianCostMs: number;
  p95CostMs: number;
  samples: number;
  gpuTimed: boolean;
  /**
   * Buffer pixels the scene and every post pass are running at, over the
   * 640x360 band's own. Above 1 on a window that is not 16:9.
   *
   * Reported rather than acted on, and that is the correct division of labour
   * rather than an omission. Both places the cost could hide already see it:
   * every number in this snapshot is measured off frames drawn at the real
   * region, and `core/bench.ts` renders the assembled chain at the real region
   * too, then keys its stored verdict on `host.pixels()` — so opening a 16:9
   * window's remembered tier in a tall one throws the verdict out and measures
   * again. What this field buys is a probe run that can say *why* a machine came
   * back a tier lower than it did last week.
   */
  fillScale: number;
}

interface LadderStep {
  label: string;
  apply(caps: PerfCaps): void;
}

/**
 * The ladder, cheapest visual cost first.
 *
 * Order is an argument about what the game is. The generative background goes
 * first and goes whole: it is the only rung that carries no gameplay
 * information at all, it sits below every other layer, and dropping it hands
 * back a full-screen procedural pass — the cheapest thing on this list to give
 * up and the only one nobody can name afterwards. Particle *density*
 * is next because a burst of 40 sparks and a burst of 24 read the same at
 * 640x360; persistence and halation follow because they are atmosphere rather
 * than information. Bloom levels come after those because narrowing the skirt
 * is the first rung a player can actually name. The lattice and the output
 * resolution are last because both are the look itself.
 *
 * Buffer *resolution* is not on this list and never will be. One world unit is
 * one buffer pixel, so shrinking the buffer would shrink what the camera shows —
 * a governor that changes how much of the board the player can see is not a
 * governor, it is a gameplay change with a frame timer attached. `REGION` sets
 * the buffer's extent once per window shape and the ladder works within it; the
 * cost of a wider one shows up in `PerfSnapshot.fillScale`.
 */
const LADDER: readonly LadderStep[] = [
  { label: 'hydra background off', apply: (c) => { c.hydra = false; } },
  { label: 'particles 60%', apply: (c) => { c.particleScale = 0.6; } },
  { label: 'particles 35%', apply: (c) => { c.particleScale = 0.35; } },
  { label: 'swarm trails 50%', apply: (c) => { c.trailScale = 0.5; } },
  { label: 'swarm trails off', apply: (c) => { c.trailScale = 0; } },
  { label: 'persistence off', apply: (c) => { c.persistence = false; } },
  { label: 'halation off', apply: (c) => { c.halation = false; } },
  { label: 'bloom 5', apply: (c) => { c.bloomLevelCap = 5; } },
  { label: 'bloom 4', apply: (c) => { c.bloomLevelCap = 4; } },
  { label: 'bloom 3', apply: (c) => { c.bloomLevelCap = 3; } },
  { label: 'grid 2x', apply: (c) => { c.gridStride = 2; } },
  { label: 'pixel ratio 1', apply: (c) => { c.pixelRatioCap = 1; } },
];

/** Rolling window, in frames. Two seconds at 60Hz — long enough for a p95. */
const WINDOW = 120;
/** Frames before the governor is allowed to touch anything. */
const WARMUP = 24;
/**
 * Integrated overrun, in seconds, that buys one rung down. Severity-weighted,
 * so a frame at twice the budget counts double and a genuinely bad load walks
 * the ladder in ~0.25s per rung instead of 0.5s.
 */
const DEGRADE_SECONDS = 0.5;
/** Overrun bleeds off at this multiple of real time while under budget. */
const BLEED = 2;
/**
 * Minimum quiet after any change before the next decision.
 *
 * This is the single most important constant in the file. At 250ms a genuinely
 * overloaded frame walked all eleven rungs in 4.4 seconds — the accumulator
 * refilled faster than the rung it had just pulled could show up in a GPU timer
 * query that is already several frames stale, so the governor was answering its
 * own question before the answer existed. At 500ms each rung is measured for
 * roughly thirty frames before the next one is considered, which puts the floor
 * at about 0.65s per rung and still clears the first rung inside half a second.
 */
const SETTLE_MS = 500;
/**
 * A rung on the p95 path needs a window that is entirely post-change, otherwise
 * the governor is reacting to frames the last rung already fixed. Two seconds
 * is the ring itself at 60Hz.
 */
const DEGRADE_WINDOW_MS = 2000;
/** Base quiet under budget before a rung comes back. */
const RESTORE_DWELL_MS = 4000;
const RESTORE_DWELL_MAX_MS = 32000;
/**
 * Degrading again inside this window means the last restore was a mistake, so
 * the restore dwell doubles. That is the anti-pump term: two bad guesses and
 * the governor is waiting 16s before it tries again.
 */
const OSCILLATION_MS = 8000;
/** Headroom. The compositor, the browser and the OS all want some of the frame. */
const BUDGET_FRACTION = 0.9;
/** Restore only with this much margin under budget. The gap is the hysteresis. */
const RESTORE_FRACTION = 0.7;
/** Wall delta above this multiple of the target counts as a dropped frame. */
const DROP_FACTOR = 1.35;
/** Above this, the frame was a tab switch or a stall, not a frame. Discard it. */
const MAX_FRAME_MS = 250;
/** Frames between windowed (sorting) evaluations. */
const EVAL_EVERY = 15;
/**
 * Consecutive real frames needed to re-arm after a capture batch.
 *
 * `main.ts` renders exactly once per animation frame, so more than one render
 * inside a single frame can only be the capture harness stepping the game by
 * hand. That is a screenshot, and a screenshot has to photograph the tuned look
 * rather than whatever the governor happened to be holding — so a batch drops
 * the ladder and stops the governor until a full second of honest frames has
 * gone by. `tools/perfprobe.mjs` seeds through the same path and re-arms as
 * soon as it hands the loop back.
 */
const CAPTURE_RELEASE = 60;
const LOG_MAX = 64;

function fullCaps(): PerfCaps {
  return {
    hydra: true,
    particleScale: 1,
    trailScale: 1,
    persistence: true,
    halation: true,
    bloomLevelCap: Infinity,
    gridStride: 1,
    pixelRatioCap: Infinity,
  };
}

export class PerfGovernor {
  /** Live caps. Read this from a render system; never write to it. */
  readonly caps: PerfCaps = fullCaps();

  /**
   * Bumped on every rung change. Consumers that have to rebuild something
   * (uniforms, output geometry, a decimation mask) compare against it rather
   * than diffing the caps themselves.
   */
  generation = 1;

  /** The `auto` mode switch. Off pins the ladder at the player's tier. */
  enabled = true;

  /** Frame budget in ms. 16.6 at 60Hz; a 120Hz display can ask for 8.3. */
  targetMs = 16.6;

  /** Rung changes since boot, newest last. Read by the perf probe. */
  readonly log: PerfEvent[] = [];

  private rung = 0;

  // ── measurement (no allocation past this point) ──
  private readonly frameRing = new Float64Array(WINDOW);
  private readonly costRing = new Float64Array(WINDOW);
  private readonly scratch = new Float64Array(WINDOW);
  private ringHead = 0;
  private ringLen = 0;
  private samples = 0;
  private evalCount = 0;
  private gpuTimed = false;

  private lastSampleMs = -1;
  private lastTimeline = Number.NaN;
  /** True while a capture harness is stepping the game by hand. */
  private batched = false;
  private clean = 0;
  private overrun = 0;
  private changedMs = -1e9;
  private restoredMs = -1e9;
  private restoreDwellMs = RESTORE_DWELL_MS;

  /** The cost we actually steer to. Target minus the headroom everyone else needs. */
  get budgetMs(): number {
    return this.targetMs * BUDGET_FRACTION;
  }

  get level(): number {
    return this.rung;
  }

  get rungCount(): number {
    return LADDER.length;
  }

  /** What the last rung pulled, for the settings readout. */
  get label(): string {
    return this.rung === 0 ? 'full quality' : LADDER[this.rung - 1].label;
  }

  setEnabled(on: boolean): void {
    if (this.enabled === on) return;
    this.enabled = on;
    // Turning the governor off hands every rung back at once: the player asked
    // for their tier, and a half-degraded frozen ladder is not their tier.
    if (!on && this.rung !== 0) this.setRung(0, -1, this.lastSampleMs);
    this.overrun = 0;
  }

  /** 16.6 for 60Hz, 8.3 for 120. Refresh rate is the caller's business. */
  setTargetMs(ms: number): void {
    const next = ms > 1 ? ms : 1;
    if (next === this.targetMs) return;
    this.targetMs = next;
    this.overrun = 0;
  }

  /**
   * Back to rung 0 with a clean window. Called when the player picks a tier:
   * their choice is the new ceiling and the governor re-earns any concession
   * from scratch rather than stacking one ladder on top of another.
   */
  reset(): void {
    this.ringHead = 0;
    this.ringLen = 0;
    this.samples = 0;
    this.evalCount = 0;
    this.lastSampleMs = -1;
    this.overrun = 0;
    this.changedMs = -1e9;
    this.restoredMs = -1e9;
    this.restoreDwellMs = RESTORE_DWELL_MS;
    if (this.rung !== 0) {
      this.rung = 0;
      this.applyRung();
      this.generation++;
    }
  }

  /**
   * Where the ladder opens, decided by the boot probe in `core/bench.ts`.
   *
   * The rung is a *starting point*, not a floor. Everything below still applies
   * to it: the governor can walk further down, and — the half that matters — it
   * restores from here the moment a window of quiet frames says the probe was
   * pessimistic. That asymmetry is what makes guessing low the safe direction.
   *
   * It also ends the boot batch. The probe renders its frames synchronously
   * inside one animation frame, which `sample` reads as a capture harness
   * stepping the game by hand — correctly, since none of them were presented —
   * and that latch would otherwise ignore the first second of the real run.
   */
  armFrom(rung: number): void {
    this.batched = false;
    this.clean = 0;
    this.lastSampleMs = -1;
    this.lastTimeline = Number.NaN;
    this.overrun = 0;

    if (!this.enabled) return;
    // Only ever the opening move: a rung already held, or a window already
    // measured, means the loop has taken over and knows more than the probe.
    if (this.rung !== 0 || this.samples > 0) return;
    const clamped = rung > LADDER.length ? LADDER.length : Math.floor(rung);
    if (clamped <= 0) return;

    this.rung = clamped;
    this.applyRung();
    this.generation++;
    this.log.push({ t: 0, rung: clamped, label: `boot probe · ${this.label}`, dir: 1 });
  }

  /** Drops the change log. The probe calls this between runs. */
  clearLog(): void {
    this.log.length = 0;
  }

  /**
   * One presented frame.
   *
   * `gpuMs` is the last clean timer query and may be a few frames stale, which
   * is fine: the governor integrates over half a second before it acts on
   * anything. `cpuMs` is the submission cost. `nowMs` is the frame's start.
   */
  sample(gpuMs: number, cpuMs: number, gpuValid: boolean, nowMs: number): void {
    // A capture batch runs many renders inside one animation frame, so the
    // document timeline does not advance across them. Those are not frames and
    // measuring them would let the governor degrade a screenshot.
    const advanced = this.timelineAdvanced();
    const prev = this.lastSampleMs;
    this.lastSampleMs = advanced ? nowMs : -1;
    if (!advanced) {
      this.batched = true;
      this.clean = 0;
      this.forceFull(nowMs);
      return;
    }
    if (this.batched && ++this.clean >= CAPTURE_RELEASE) this.batched = false;
    if (prev < 0) return;

    const frameMs = nowMs - prev;
    if (!(frameMs > 0) || frameMs > MAX_FRAME_MS) return;

    this.gpuTimed = gpuValid;
    // GPU and CPU overlap, so the frame costs whichever finishes last. Without
    // a timer query the GPU is invisible and only dropped frames give it away.
    const costMs = gpuValid ? (gpuMs > cpuMs ? gpuMs : cpuMs) : cpuMs;

    this.frameRing[this.ringHead] = frameMs;
    this.costRing[this.ringHead] = costMs;
    this.ringHead = this.ringHead + 1 === WINDOW ? 0 : this.ringHead + 1;
    if (this.ringLen < WINDOW) this.ringLen++;
    this.samples++;

    if (!this.enabled || this.batched) return;

    const budget = this.budgetMs;
    // Two independent tells. Cost catches a GPU that is saturated behind vsync,
    // where the wall delta pegs at 16.7 and says nothing; the wall delta catches
    // everything the timer cannot see, including the sim itself.
    const dropped = frameMs > this.targetMs * DROP_FACTOR;
    const dt = (frameMs > 50 ? 50 : frameMs) / 1000;
    if (costMs > budget || dropped) {
      let severity = costMs / budget;
      if (severity < 1) severity = 1;
      else if (severity > 4) severity = 4;
      this.overrun += dt * severity;
    } else {
      this.overrun -= dt * BLEED;
      if (this.overrun < 0) this.overrun = 0;
    }

    if (this.samples < WARMUP) return;
    if (nowMs - this.changedMs < SETTLE_MS) return;

    if (this.overrun >= DEGRADE_SECONDS) {
      this.setRung(this.rung + 1, 1, nowMs);
      return;
    }

    this.evalCount++;
    if (this.evalCount < EVAL_EVERY) return;
    this.evalCount = 0;
    if (this.ringLen < WINDOW) return;
    if (this.maybeDegrade(nowMs)) return;
    this.maybeRestore(nowMs);
  }

  /** A copy of the current state. For the settings readout and the perf probe. */
  snapshot(): PerfSnapshot {
    return {
      enabled: this.enabled,
      rung: this.rung,
      rungs: LADDER.length,
      label: this.label,
      targetMs: this.targetMs,
      budgetMs: this.budgetMs,
      medianFrameMs: this.percentile(this.frameRing, 0.5),
      p95FrameMs: this.percentile(this.frameRing, 0.95),
      medianCostMs: this.percentile(this.costRing, 0.5),
      p95CostMs: this.percentile(this.costRing, 0.95),
      samples: this.samples,
      gpuTimed: this.gpuTimed,
      fillScale: region.fillScale,
    };
  }

  // ─────────────────────────────────────────────────────────────── internals ──

  /**
   * The tail path.
   *
   * The overrun accumulator answers "is this bad *now*", and it is deliberately
   * hard to move: one hitch in twenty bleeds off before it can buy a rung. But
   * one frame in twenty over the budget is exactly the judder the player is
   * complaining about, and no accumulator with a 2x bleed will ever see it. So
   * the 95th percentile gets a rule of its own, over a window that is entirely
   * post-change, and it is the same statistic the restore rule reads — which is
   * what makes the hysteresis band a band and not two unrelated thresholds.
   */
  private maybeDegrade(nowMs: number): boolean {
    if (this.rung >= LADDER.length) return false;
    if (nowMs - this.changedMs < DEGRADE_WINDOW_MS) return false;
    if (this.percentile(this.costRing, 0.95) <= this.budgetMs) return false;
    this.setRung(this.rung + 1, 1, nowMs);
    return true;
  }

  private maybeRestore(nowMs: number): void {
    if (this.rung === 0) return;
    if (nowMs - this.changedMs < this.restoreDwellMs) return;
    // Any pressure at all in the recent past disqualifies a restore outright.
    if (this.overrun > 0) return;
    if (this.percentile(this.costRing, 0.95) >= this.budgetMs * RESTORE_FRACTION) return;
    if (this.percentile(this.frameRing, 0.95) > this.targetMs * 1.2) return;
    this.setRung(this.rung - 1, -1, nowMs);
  }

  private setRung(next: number, dir: 1 | -1, nowMs: number): void {
    const clamped = next < 0 ? 0 : next > LADDER.length ? LADDER.length : next;
    if (clamped === this.rung) return;

    // Degrading again right after a restore means the restore was wrong. Make
    // the next one wait twice as long, and keep doubling until it stops.
    if (dir > 0 && nowMs - this.restoredMs < OSCILLATION_MS) {
      const doubled = this.restoreDwellMs * 2;
      this.restoreDwellMs = doubled > RESTORE_DWELL_MAX_MS ? RESTORE_DWELL_MAX_MS : doubled;
    }

    this.rung = clamped;
    this.changedMs = nowMs;
    if (dir < 0) this.restoredMs = nowMs;
    this.overrun = 0;
    this.applyRung();
    this.generation++;

    if (this.log.length >= LOG_MAX) this.log.shift();
    this.log.push({ t: nowMs, rung: clamped, label: this.label, dir });
  }

  /**
   * Hands every rung back without touching the dwell bookkeeping.
   *
   * It still writes to the log. Without the entry a trace reads as two descents
   * to rung 1 with no ascent between them, which looks like a governor that
   * lost count rather than one that stood aside for a screenshot.
   */
  private forceFull(nowMs: number): void {
    if (this.rung === 0) return;
    this.rung = 0;
    this.overrun = 0;
    this.applyRung();
    this.generation++;
    if (this.log.length >= LOG_MAX) this.log.shift();
    this.log.push({ t: nowMs, rung: 0, label: 'capture · full quality', dir: -1 });
  }

  private applyRung(): void {
    const c = this.caps;
    const full = fullCaps();
    c.hydra = full.hydra;
    c.particleScale = full.particleScale;
    c.trailScale = full.trailScale;
    c.persistence = full.persistence;
    c.halation = full.halation;
    c.bloomLevelCap = full.bloomLevelCap;
    c.gridStride = full.gridStride;
    c.pixelRatioCap = full.pixelRatioCap;
    for (let i = 0; i < this.rung; i++) LADDER[i].apply(c);
  }

  /**
   * True when a real presentation happened since the last call.
   *
   * `document.timeline.currentTime` is frozen for the whole of one rendering
   * update, so a synchronous loop of renders — which is exactly what the
   * capture harness does — reads the same value every time.
   */
  private timelineAdvanced(): boolean {
    if (typeof document === 'undefined') return true;
    const t = document.timeline?.currentTime;
    if (typeof t !== 'number') return true;
    if (t === this.lastTimeline) return false;
    this.lastTimeline = t;
    return true;
  }

  /** Insertion sort over a preallocated scratch. No allocation, no `Array.sort`. */
  private percentile(ring: Float64Array, q: number): number {
    const n = this.ringLen;
    if (n === 0) return 0;
    const s = this.scratch;
    for (let i = 0; i < n; i++) s[i] = ring[i];
    for (let i = 1; i < n; i++) {
      const v = s[i];
      let j = i - 1;
      while (j >= 0 && s[j] > v) {
        s[j + 1] = s[j];
        j--;
      }
      s[j + 1] = v;
    }
    let k = Math.round(q * (n - 1));
    if (k < 0) k = 0;
    else if (k >= n) k = n - 1;
    return s[k];
  }
}

/** The one governor. Everything that measures or obeys it talks to this. */
export const perf = new PerfGovernor();

/** Frame budget for a refresh rate, e.g. `targetForHz(120)` → 8.33. */
export function targetForHz(hz: number): number {
  return 1000 / (hz > 1 ? hz : 60);
}

// Debug surface for tools/perfprobe.mjs. Read-only in practice; the probe uses
// it to read the rolling stats and the rung log out of a live page.
if (typeof window !== 'undefined') {
  (window as unknown as { __pixeltdPerf?: PerfGovernor }).__pixeltdPerf = perf;
}
